import { flushSync } from 'react-dom';

import type {
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  RefObject
} from 'react';

import type { SpotHistoryTableColumnType } from '@/types/markets';

import { COLUMN_GAP, DATA_COLUMN_WIDTH, RESIZE_KEYBOARD_STEP } from './constants';
import type { SetColumnWidth } from './useColumnWidths';
import { clampColumnWidth, getColumnCode } from './utils';

type ResizeOptions = {
  scrollRef: RefObject<HTMLDivElement | null>;
  totalWidth: number;
  setColumnWidth: SetColumnWidth;
  onResizeEnd: (shouldSuppressClick: boolean) => void;
};

type PointerContext = {
  column: SpotHistoryTableColumnType;
  currentWidth: number;
  columnOrder: number;
};

type Dom = {
  root: HTMLElement;
  inner: HTMLElement | null;
  columnCode: string;
  columnOrder: number;
};

function getResizeColumnElements(root: HTMLElement, columnCode: string) {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-resize-col]')).filter(
    el => el.dataset.resizeCol === columnCode
  );
}

function applyResizeDom(
  { root, inner, columnCode, columnOrder }: Dom,
  startWidth: number,
  effectiveDelta: number,
  newTotal: number
) {
  getResizeColumnElements(root, columnCode).forEach(el => {
    el.style.width = `${startWidth + effectiveDelta - COLUMN_GAP}px`;
  });
  root.querySelectorAll<HTMLElement>('[data-resize-col-order]').forEach(el => {
    const order = Number(el.dataset.resizeColOrder);
    if (order > columnOrder) {
      el.style.transform = `translateX(${effectiveDelta}px)`;
    }
  });
  root.querySelectorAll<HTMLElement>('[role="rowgroup"], [role="row"]').forEach(el => {
    el.style.width = `${newTotal}px`;
  });
  if (inner) inner.style.minWidth = `${newTotal}px`;
}

function clearResizeDom({ root, inner, columnCode }: Dom) {
  getResizeColumnElements(root, columnCode).forEach(el => {
    el.style.width = '';
  });
  root.querySelectorAll<HTMLElement>('[data-resize-col-order]').forEach(el => {
    el.style.transform = '';
  });
  root.querySelectorAll<HTMLElement>('[role="rowgroup"], [role="row"]').forEach(el => {
    el.style.width = '';
  });
  if (inner) inner.style.minWidth = '';
}

export function createResizePointerHandler(
  { scrollRef, totalWidth, setColumnWidth, onResizeEnd }: ResizeOptions,
  { column, currentWidth, columnOrder }: PointerContext
) {
  return (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = currentWidth;
    const startTotalWidth = totalWidth;
    const root = scrollRef.current;
    if (!root) return;

    const columnCode = getColumnCode(column);
    if (!columnCode) return;

    const dom: Dom = {
      root,
      inner: (root.firstElementChild as HTMLElement | null) ?? null,
      columnCode,
      columnOrder
    };

    let latestWidth = startWidth;
    let pendingFrame = 0;
    let pendingDelta = 0;
    let didDrag = false;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handlePointerMove = (moveEvent: PointerEvent) => {
      didDrag ||= Math.abs(moveEvent.clientX - startX) > 2;
      latestWidth = clampColumnWidth(startWidth + moveEvent.clientX - startX);
      pendingDelta = latestWidth - startWidth;
      if (pendingFrame) return;
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = 0;
        applyResizeDom(dom, startWidth, pendingDelta, startTotalWidth + pendingDelta);
      });
    };

    const cleanupResize = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      if (pendingFrame) {
        cancelAnimationFrame(pendingFrame);
        pendingFrame = 0;
      }
    };

    const handlePointerUp = () => {
      cleanupResize();
      const scrollLeft = root.scrollLeft;

      const didResize = latestWidth !== startWidth;
      if (didResize) {
        flushSync(() => {
          setColumnWidth(columnCode, latestWidth);
        });
      }
      clearResizeDom(dom);
      root.scrollLeft = scrollLeft;
      onResizeEnd(didDrag || didResize);
    };

    const handlePointerCancel = () => {
      cleanupResize();
      clearResizeDom(dom);
      onResizeEnd(didDrag);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);
  };
}

export function createResizeKeyDownHandler(
  setColumnWidth: SetColumnWidth,
  column: SpotHistoryTableColumnType,
  currentWidth: number
) {
  return (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const columnCode = getColumnCode(column);
    if (!columnCode) return;

    setColumnWidth(columnCode, currentWidth + direction * RESIZE_KEYBOARD_STEP);
  };
}

export function createResizeDoubleClickHandler(
  setColumnWidth: SetColumnWidth,
  column: SpotHistoryTableColumnType
) {
  return (event: ReactMouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const columnCode = getColumnCode(column);
    if (!columnCode) return;

    setColumnWidth(columnCode, DATA_COLUMN_WIDTH);
  };
}
